import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  KeyRound,
  Plus,
  Radio,
  Trash2,
} from 'lucide-react';
import {
  type CreatedSubscription,
  type DeliveryRow,
  type EventSubscriptionRow,
  createSubscription,
  deleteSubscription,
  listDeliveries,
  listEventCatalog,
  listSubscriptions,
  updateSubscription,
} from '@/api/events';
import { errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-900/40 text-yellow-300',
  DELIVERING: 'bg-blue-900/40 text-blue-300',
  SUCCESS: 'bg-green-900/40 text-green-300',
  FAILED: 'bg-red-900/40 text-red-300',
  DEAD_LETTER: 'bg-muted text-foreground',
};

export function EventSubscriptionsPage() {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<EventSubscriptionRow[]>([]);
  const [catalog, setCatalog] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [secret, setSecret] = useState<CreatedSubscription | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listSubscriptions()
      .then(setSubscriptions)
      .catch((err) => setError(errorDetail(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    listEventCatalog()
      .then(setCatalog)
      .catch(() => setCatalog([]));
  }, [load]);

  async function handleDelete(id: string) {
    if (!confirm(t('eventSubscriptions.deleteConfirm'))) return;
    try {
      await deleteSubscription(id);
      load();
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  async function handleToggle(s: EventSubscriptionRow) {
    try {
      await updateSubscription(s.id, { enabled: !s.enabled });
      load();
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  function handleCreated(created: CreatedSubscription) {
    setFormOpen(false);
    setSecret(created);
    load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('eventSubscriptions.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
            {t('eventSubscriptions.subtitle')}
          </p>
        </div>
        <Button size="sm" onClick={() => setFormOpen(true)} data-testid="event-sub-new">
          <Plus className="mr-1.5 h-4 w-4" />
          {t('eventSubscriptions.create')}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      {secret && <SecretBanner secret={secret.secret} onDismiss={() => setSecret(null)} />}

      {formOpen && (
        <CreateSubscriptionForm
          catalog={catalog}
          onCreated={handleCreated}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : subscriptions.length === 0 && !formOpen ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('eventSubscriptions.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {subscriptions.map((s) => (
            <SubscriptionRow
              key={s.id}
              subscription={s}
              expanded={expandedId === s.id}
              onToggleExpand={() => setExpandedId(expandedId === s.id ? null : s.id)}
              onToggleEnabled={() => handleToggle(s)}
              onDelete={() => handleDelete(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubscriptionRow({
  subscription,
  expanded,
  onToggleExpand,
  onToggleEnabled,
  onDelete,
}: {
  subscription: EventSubscriptionRow;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleEnabled: () => void;
  onDelete: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [dlLoading, setDlLoading] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    setDlLoading(true);
    listDeliveries(subscription.id)
      .then(setDeliveries)
      .catch(() => setDeliveries([]))
      .finally(() => setDlLoading(false));
  }, [expanded, subscription.id]);

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-start gap-4 p-4">
        <button
          onClick={onToggleExpand}
          className="mt-0.5 shrink-0 text-muted-foreground"
          aria-label={t('eventSubscriptions.deliveries.title')}
          data-testid="event-sub-expand"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <Radio className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm font-medium text-[var(--color-lop-text-primary)]">
              {subscription.eventPattern}
            </code>
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                subscription.enabled
                  ? 'bg-green-900/40 text-green-300'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {subscription.enabled
                ? t('eventSubscriptions.enabled')
                : t('eventSubscriptions.disabled')}
            </span>
            {subscription.privacyParticipant && (
              <span
                className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300"
                title={t('eventSubscriptions.privacyParticipantHint')}
              >
                {t('eventSubscriptions.privacyParticipant')}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="max-w-md truncate">{subscription.handlerUrl}</span>
            <span>
              {t('eventSubscriptions.retryMaxAttempts')}: {subscription.retryMaxAttempts}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="ghost" onClick={onToggleEnabled} data-testid="event-sub-toggle">
            {subscription.enabled
              ? t('eventSubscriptions.disable')
              : t('eventSubscriptions.enable')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title={t('common.delete')}
            onClick={onDelete}
            data-testid="event-sub-delete"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('eventSubscriptions.deliveries.title')}
          </div>
          {dlLoading ? (
            <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
          ) : deliveries.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              {t('eventSubscriptions.deliveries.empty')}
            </div>
          ) : (
            <div className="space-y-1">
              {deliveries.map((d) => (
                <div
                  key={d.id}
                  className="flex flex-wrap items-center gap-3 rounded-md bg-background px-3 py-2 text-xs"
                >
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      STATUS_COLORS[d.status] ?? 'bg-muted text-muted-foreground',
                    )}
                  >
                    {t(`eventSubscriptions.deliveries.statuses.${d.status}`, {
                      defaultValue: d.status,
                    })}
                  </span>
                  <code className="font-mono text-[var(--color-lop-text-secondary)]">
                    {d.eventName}
                  </code>
                  <span className="text-muted-foreground">
                    {t('eventSubscriptions.deliveries.attempt')} #{d.attempt}
                  </span>
                  {d.responseStatus !== null && (
                    <span className="text-muted-foreground">HTTP {d.responseStatus}</span>
                  )}
                  {d.lastError && <span className="truncate text-destructive">{d.lastError}</span>}
                  <span className="ml-auto shrink-0 text-muted-foreground">
                    {new Date(d.createdAt).toLocaleString(i18n.language)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function SecretBanner({ secret, onDismiss }: { secret: string; onDismiss: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the field stays selectable */
    }
  }

  return (
    <Card tier="raised" className="border-amber-500/40 bg-amber-500/5" data-testid="event-sub-secret-banner">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-300">
          {t('eventSubscriptions.secret.title')}
        </h3>
      </div>
      <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
        {t('eventSubscriptions.secret.warning')}
      </p>
      <div className="mt-3 flex gap-2">
        <Input
          value={secret}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono"
        />
        <Button size="sm" onClick={copy} data-testid="event-sub-secret-copy">
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" variant="outline" onClick={onDismiss}>
          {t('common.close')}
        </Button>
      </div>
    </Card>
  );
}

function CreateSubscriptionForm({
  catalog,
  onCreated,
  onCancel,
}: {
  catalog: string[];
  onCreated: (s: CreatedSubscription) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [eventPattern, setEventPattern] = useState('');
  const [handlerUrl, setHandlerUrl] = useState('');
  const [retryMaxAttempts, setRetryMaxAttempts] = useState(5);
  const [privacyParticipant, setPrivacyParticipant] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await createSubscription({
        eventPattern: eventPattern.trim(),
        handlerUrl: handlerUrl.trim(),
        retryMaxAttempts,
        privacyParticipant,
      });
      onCreated(created);
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="mb-4 font-medium">{t('eventSubscriptions.createModal.title')}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium">
            {t('eventSubscriptions.eventPattern')}
          </label>
          <Input
            value={eventPattern}
            onChange={(e) => setEventPattern(e.target.value)}
            placeholder={t('eventSubscriptions.eventPatternPlaceholder')}
            required
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t('eventSubscriptions.eventPatternHint')}
          </p>
          {catalog.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[11px] text-muted-foreground">
                {t('eventSubscriptions.catalogHint')}:
              </span>
              {catalog.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setEventPattern(name)}
                  className="rounded bg-[var(--color-lop-bg-card-hi)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-lop-text-secondary)] hover:text-[var(--color-lop-teal)]"
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            {t('eventSubscriptions.handlerUrl')}
          </label>
          <Input
            value={handlerUrl}
            onChange={(e) => setHandlerUrl(e.target.value)}
            placeholder="https://..."
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            {t('eventSubscriptions.retryMaxAttempts')}
          </label>
          <Input
            type="number"
            min={1}
            max={10}
            value={retryMaxAttempts}
            onChange={(e) => setRetryMaxAttempts(Number(e.target.value))}
            className="max-w-[120px]"
          />
        </div>
        <div>
          <label className="flex items-start gap-2 text-xs font-medium">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={privacyParticipant}
              onChange={(e) => setPrivacyParticipant(e.target.checked)}
              data-testid="event-sub-privacy-participant"
            />
            <span>
              {t('eventSubscriptions.privacyParticipant')}
              <span className="mt-1 block font-normal text-[11px] text-muted-foreground">
                {t('eventSubscriptions.privacyParticipantHint')}
              </span>
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={saving || !eventPattern.trim() || !handlerUrl.trim()}
            data-testid="event-sub-create"
          >
            {saving ? t('common.saving') : t('eventSubscriptions.createModal.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
